import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { usersAPI, ordersAPI } from '../services/api';
import AccountSettings from '../components/AccountSettings';
import './ProfilePage.css';

const TABS = {
  ORDERS: 'ORDERS',
  SETTINGS: 'SETTINGS'
};

const ProfilePage = () => {

  const [activeTab, setActiveTab] = useState(TABS.ORDERS);
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const userResponse = await usersAPI.getProfile();
        let resolvedUser = userResponse || null;

        if (!resolvedUser) {
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            resolvedUser = JSON.parse(storedUser);
          }
        }

        setUser(resolvedUser);

        if (resolvedUser?.id) {
          const ordersResponse = await ordersAPI.getByUser(resolvedUser.id);
          const normalizedOrdersRaw = Array.isArray(ordersResponse)
            ? ordersResponse
            : ordersResponse?.content || ordersResponse?.orders || ordersResponse?.data || [];

          const normalizedOrders = [...normalizedOrdersRaw].sort((a, b) => {
            const aDate = new Date(a.createdAt || 0).getTime();
            const bDate = new Date(b.createdAt || 0).getTime();
            return bDate - aDate; // newest first
          });
          setOrders(normalizedOrders);
        } else {
          setOrders([]);
        }
      } catch (err) {
        console.error('Profile load error:', err);
        setError('Unable to load profile right now. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const formattedSince = useMemo(() => {
    if (!user?.createdAt) return '';
    return `Member since ${new Date(user.createdAt).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    })}`;
  }, [user]);

  const currentInitials = useMemo(() => {
    if (!user) return 'TP';
    if (user.fullName) {
      return user.fullName
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('');
    }
    const firstInitial = user.firstName?.[0]?.toUpperCase() || '';
    const lastInitial = user.lastName?.[0]?.toUpperCase() || '';
    return (firstInitial + lastInitial) || 'TP';
  }, [user]);

  const getStatusColor = (status) => {
    const normalized = status?.toUpperCase();
    if (normalized === 'COMPLETED') return '#34c759';
    if (normalized === 'PENDING') return '#ff9f0a';
    if (normalized === 'CANCELLED') return '#ff3b30';
    return '#8e8e93';
  };

  const groupItems = (order) => {
    const items = order.orderItems || order.items || [];
    const grouped = {};
    items.forEach((item) => {
      const name = item.ticketTypeName || item.ticketTypeCategory || 'Ticket';
      const qty = Number(item.quantity) || 0;
      if (!grouped[name]) {
        grouped[name] = 0;
      }
      grouped[name] += qty;
    });
    return Object.entries(grouped).map(([name, qty]) => ({ name, qty }));
  };

  const getOrderEventMeta = (order) => {
    const firstItem = (order.orderItems || order.items || [])[0] || {};
    const title =
      firstItem.eventTitle ||
      firstItem.eventName ||
      firstItem.event?.title ||
      firstItem.event?.name ||
      order.eventTitle ||
      order.eventName ||
      'Event details';

    const date =
      firstItem.eventDate ||
      firstItem.eventTime ||
      order.eventDate ||
      order.eventTime ||
      firstItem.createdAt ||
      order.createdAt;

    const venueCandidates = [
      firstItem.venueName,
      firstItem.venue?.name,
      firstItem.eventVenueName,
      firstItem.event?.venueName,
      firstItem.event?.venue?.name,
      firstItem.ticketType?.event?.venue?.name,
      firstItem.ticketType?.eventVenueName,
      order.venueName,
      order.eventVenue,
      order.event?.venueName,
      order.event?.venue?.name,
      order.eventVenueName
    ];

    const venue = venueCandidates.find(Boolean) || null;

    return { title, date, venue };
  };

  const downloadTickets = useCallback(async (orderId) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('You need to be signed in');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/tickets/order/${orderId}/download`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `order-${String(orderId).slice(0, 8)}-tickets.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadErr) {
      console.error('Ticket download error:', downloadErr);
      setError('Unable to download tickets. Please try again later.');
    }
  }, []);

  if (loading) {
    return (
      <div className="profile-shell">
        <div className="profile-container">
          <div className="profile-empty-orders">Loading profile…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-shell">
        <div className="profile-container">
          <div className="profile-empty-orders">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-shell">
      <Helmet>
        <title>Profile - TikPlat</title>
        <meta name="description" content="Manage your account, review orders, and adjust settings on TikPlat." />
      </Helmet>

      <div className="profile-container">
        <div className="profile-sidebar-column">
          <section className="profile-card">
            <div className="profile-avatar" aria-hidden>{currentInitials}</div>
            <div className="profile-heading">
              <h1>{user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'TikPlat Member'}</h1>
              <span>{user?.email}</span>
            </div>
            {formattedSince && <div className="profile-callout">{formattedSince}</div>}
          </section>

          <nav className="profile-menu">
            {Object.values(TABS).map((tab) => (
              <button
                type="button"
                key={tab}
                className="profile-menu-item"
                data-active={activeTab === tab}
                onClick={() => setActiveTab(tab)}
              >
                {tab === TABS.ORDERS && 'Orders'}
                {tab === TABS.SETTINGS && 'Settings'}
              </button>
            ))}
          </nav>
        </div>

        <section className="profile-main-panel">
          <header className="profile-main-header">
            <h2>
              {activeTab === TABS.ORDERS && 'Your orders'}
              {activeTab === TABS.SETTINGS && 'Account settings'}
            </h2>
            <p>
              {activeTab === TABS.ORDERS && 'Recent tickets and order history'}
              {activeTab === TABS.SETTINGS && 'Manage your profile, security, and preferences'}
            </p>
          </header>

          <div className="profile-main-body">
            {activeTab === TABS.ORDERS && (
              <>
                {orders.length === 0 ? (
                  <div className="profile-empty-orders">
                    <div className="icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
                        <path d="M9 12h6" />
                      </svg>
                    </div>
                    No orders yet. Start booking your next event adventure.
                  </div>
                ) : (
                  orders.map((order) => {
                    const statusColor = getStatusColor(order.status);
                    const statusStyles = {
                      color: statusColor,
                      backgroundColor: `${statusColor}22`
                    };

                    return (
                      <article className="profile-order-card" key={order.id}>
                        <div className="profile-order-header">
                          <div>
                            <div className="order-number">Order #{order.orderNumber || order.id}</div>
                            <div className="order-date">
                              {new Date(order.createdAt).toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                          <span className="profile-order-status" style={statusStyles}>
                            {order.status?.toLowerCase()}
                          </span>
                        </div>

                        {(() => {
                          const { title, date, venue } = getOrderEventMeta(order);
                          return (
                            <div className="profile-order-event">
                              <div className="profile-order-event-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
                                </svg>
                              </div>
                              <div className="profile-order-event-meta">
                                <div className="event-title">
                                  {title}
                                  {venue && (
                                    <span className="event-subtitle"> @ {venue}</span>
                                  )}
                                  {date && (
                                    <span className="event-subtitle">
                                      {' '}· {new Date(date).toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        <div className="profile-order-items">
                          {groupItems(order).map((item) => (
                            <div key={item.name}>• {item.name} × {item.qty}</div>
                          ))}
                        </div>

                        <div className="profile-order-total-row">
                          <span>Total paid</span>
                          <strong>{(order.currencyCode || 'USD').toUpperCase()} {Number(order.totalAmount || 0).toFixed(2)}</strong>
                        </div>

                        <div className="profile-order-actions">
                          <button
                            type="button"
                            className="profile-download-button"
                            onClick={() => downloadTickets(order.id)}
                          >
                            ⬇ Download tickets
                          </button>
                        </div>
                      </article>
                    );
                  })
                )}
              </>
            )}

            {activeTab === TABS.SETTINGS && (
              <AccountSettings user={user} onUserUpdated={setUser} />
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProfilePage;
